"use client";

import { Accessibility, CarFront, Rocket, Snail, Zap } from "lucide-react";

import { cn } from "../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export default function ShippingSpeedRanking({
  note,
  size = "xs",
}: {
  note: number
  size?: "sm" | "md" | "lg" | "xs"
}) {
  function getSize() {
    switch (size) {
      case "xs":
        return "w-5 h-5"
      case "sm":
        return "w-6 h-6"
      case "md":
        return "w-8 h-8"
      case "lg":
        return "w-10 h-10"
    }
  }

  function getFontSize() {
    switch (size) {
      case "xs":
        return "text-sm"
      case "sm":
        return "text-base"
      case "md":
        return "text-base"
      case "lg":
        return "text-lg"
    }
  }

  return (
    <div className={cn("flex gap-2 h-fit items-center text-main-black font-medium", getFontSize())}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn("flex h-fit w-fit gap-2 items-center text-main-black font-medium rounded-md bg-red-400 p-2", {
              "opacity-60 grayscale": note < 1,
            })}
          >
            <Snail className={cn("shrink-0 text-main-black", getSize())} />
            {note === 1 && <span>Slow as hell</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>Slow as hell</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex h-fit w-fit gap-2 items-center text-main-black font-medium rounded-md bg-orange-400 p-2",
              {
                "opacity-60 grayscale": note < 2,
              }
            )}
          >
            <Accessibility
              className={cn("shrink-0 text-main-black", getSize())}
            />
            {note === 2 && <span>Slow</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>Slow</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex h-fit w-fit gap-2 items-center text-main-black font-medium rounded-md bg-amber-400 p-2",
              {
                "opacity-60 grayscale": note < 3,
              }
            )}
          >
            <CarFront className={cn("shrink-0 text-main-black", getSize())} />
            {note === 3 && <span>Fast</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>Fast</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex h-fit w-fit gap-2 items-center text-main-black font-medium rounded-md bg-yellow-400 p-2",
              {
                "opacity-60 grayscale": note < 4,
              }
            )}
          >
            <Rocket className={cn("shrink-0 text-main-black", getSize())} />
            {note === 4 && <span>Fast as hell</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>Fast as hell</TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              "flex h-fit w-fit gap-2 items-center text-main-black font-medium rounded-md bg-green-400 p-2",
              {
                "opacity-60 grayscale": note < 5,
              }
            )}
          >
            <Zap className={cn("shrink-0 text-main-black", getSize())} />
            {note === 5 && <span>Light-speed</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent>Light-speed</TooltipContent>
      </Tooltip>
    </div>
  )
}
